/**
 * PortalTooltip — Reusable tooltip wrapper for portal components.
 *
 * Wraps any element with a Material-UI Tooltip whose content is sourced
 * from the TooltipContext (DB-backed or static fallback).
 *
 * Usage:
 *   <PortalTooltip tooltipKey="sme.dashboard.pending_validations">
 *     <Paper>...</Paper>
 *   </PortalTooltip>
 */
import React from 'react';
import { Tooltip, Typography, Box } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useTooltips } from '../contexts/TooltipContext';

interface PortalTooltipProps {
  /** The component key matching a tooltip in the database */
  tooltipKey: string;
  /** Tooltip placement (default: top) */
  placement?:
    | 'top' | 'top-start' | 'top-end'
    | 'bottom' | 'bottom-start' | 'bottom-end'
    | 'left' | 'left-start' | 'left-end'
    | 'right' | 'right-start' | 'right-end';
  /** Whether to show a small info icon next to children (default: false) */
  showIcon?: boolean;
  /** The wrapped element */
  children: React.ReactElement;
}

const PortalTooltip: React.FC<PortalTooltipProps> = ({
  tooltipKey,
  placement = 'top',
  showIcon = false,
  children,
}) => {
  const { getTooltip } = useTooltips();
  const tip = getTooltip(tooltipKey);

  if (!tip) {
    return children;
  }

  const tooltipContent = (
    <Box sx={{ maxWidth: 320, p: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.8rem' }}>
        {tip.title}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
        {tip.description}
      </Typography>
    </Box>
  );

  if (showIcon) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {children}
        <Tooltip title={tooltipContent} placement={placement} arrow>
          <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      </Box>
    );
  }

  return (
    <Tooltip title={tooltipContent} placement={placement} arrow>
      {children}
    </Tooltip>
  );
};

export default PortalTooltip;
